import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BusinessPartnerDto, CustomerDto, useProductCenterApi } from 'api';
import { searchBusinessPartnersQuery } from 'api/queries';
import { VolvoDataGrid } from 'library/components/controls/datagrid/VolvoDataGrid';
import { SearchProductForm } from 'library/components/controls/search/SearchForm/SearchProductForm';
import { useValidationErrorsSnackbar } from 'library/hooks/useValidationErrorsSnackbar';
import { selectUser } from 'library/redux/application';
import { useAppSelector } from 'library/redux/hooks';
import { Sxable } from 'library/types';
import {
	CountryCell,
	getFilterOperators,
} from 'modules/EndCustomer/Shared/DataGrid/CellRender/CountryCell';
import { LocationCountryText } from 'modules/Shared/Components/OperatingLocation/LocationCountryText';

export const BusinessPartnerAccordionSection = ({
	onlySelectionOfCustomer = false,
	assignedCustomerHeader = 'Currently assigned Customer:',
	initialDisplayedCustomer = null,
	padding = 2,
	forcedDisplayedCustomer = null,
	qaAccessForm = false,
	showCountryCode = true,
	clearSearchTrigger = 0,
	onCustomerSelected,
}: PropsWithChildren<{
	onlySelectionOfCustomer?: boolean;
	assignedCustomerHeader?: string;
	initialDisplayedCustomer?: CustomerDto | null;
	padding?: number;
	forcedDisplayedCustomer?: CustomerDto | null;
	qaAccessForm?: boolean;
	clearSearchTrigger?: number;
	showCountryCode?: boolean;
	onCustomerSelected?: (customer: CustomerDto | null) => void;
}> &
	Sxable) => {
	const { t } = useTranslation([
		'home',
		'common',
		'commonButton',
		'maintenancePlanner',
		'searchProduct',
		'error',
		'qaAccess',
	]);
	const { enqueueValidationSnackbar } = useValidationErrorsSnackbar();
	const { customer } = useAppSelector(selectUser);
	const [displayedCustomer, setDisplayedCustomer] =
		useState<CustomerDto | null>(customer);

	const api = useProductCenterApi().api;

	const [businessPartnerSearch, setBusinessPartnerSearch] = useState('');
	const [selectedBusinessPartnerId, setSelectedBusinessPartnerId] =
		useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });
	const [assigningCustomer, setAssigningCustomer] = useState(false);

	const {
		data: businessPartnerRows = [],
		isLoading: isLoadingBusinessPartners,
	} = useQuery(
		searchBusinessPartnersQuery(api, businessPartnerSearch, qaAccessForm)
	);

	const selectedBusinessPartner = useMemo(() => {
		const selectedRowId = selectedBusinessPartnerId.ids
			.values()
			.toArray()[0];
		if (!selectedRowId) return null;

		return (
			businessPartnerRows.find((row) => row.id === selectedRowId) ?? null
		);
	}, [selectedBusinessPartnerId, businessPartnerRows]);

	const isSelectedBusinessPartnerAlreadyAssigned =
		selectedBusinessPartner != null &&
		displayedCustomer != null &&
		String(selectedBusinessPartner.customerNumber) ===
			String(displayedCustomer.customerNumber);

	useEffect(() => {
		if (!onlySelectionOfCustomer) return;

		setDisplayedCustomer(initialDisplayedCustomer);
		onCustomerSelected?.(initialDisplayedCustomer ?? null);
	}, [onlySelectionOfCustomer, initialDisplayedCustomer, onCustomerSelected]);

	useEffect(() => {
		if (!onlySelectionOfCustomer) return;

		const nextCustomer =
			forcedDisplayedCustomer ?? initialDisplayedCustomer ?? null;
		setDisplayedCustomer(nextCustomer);
	}, [
		onlySelectionOfCustomer,
		forcedDisplayedCustomer,
		initialDisplayedCustomer,
	]);

	useEffect(() => {
		setBusinessPartnerSearch('');
		setSelectedBusinessPartnerId({ type: 'include', ids: new Set() });
	}, [clearSearchTrigger]);

	const businessPartnerColumns = useMemo(
		(): GridColDef<BusinessPartnerDto>[] => [
			{
				field: 'customerName',
				headerName: t(
					'maintenancePlanner:search-business-partner-helper-text'
				),
				flex: 2,
				minWidth: 250,
			},
			{
				field: 'customerNumber',
				headerName: t('maintenancePlanner:business-partner-number'),
				flex: 1,
				minWidth: 120,
			},
			{
				field: 'countryCode',
				headerName: t('common:country'),
				flex: 2,
				minWidth: 60,
				renderCell: ({ row: { countryCode } }) => (
					<CountryCell countryCode={countryCode} />
				),
				filterOperators: getFilterOperators,
				align: 'left',
				display: 'flex',
			},
			{
				field: 'city',
				headerName: t('common:city'),
				flex: 2,
				minWidth: 140,
			},
		],
		[t]
	);

	const handleSendCustomer = async () => {
		const selectedRows = selectedBusinessPartnerId.ids
			.values()
			.toArray()[0];

		if (!selectedRows) {
			return;
		}

		const selectedCustomer = businessPartnerRows.find(
			(row) => row.id === selectedRows
		);
		if (!selectedCustomer) return;

		setAssigningCustomer(true);

		//For QaAccessRequestForm
		if (onlySelectionOfCustomer) {
			try {
				const { data: fullSelectedCustomer } =
					await api.getSingleBusinessPartnerForQaAccess({
						customerNumber: selectedCustomer.customerNumber,
					});

				if (fullSelectedCustomer) {
					setDisplayedCustomer(fullSelectedCustomer);
					onCustomerSelected?.(fullSelectedCustomer);
				}
			} catch (error) {
				enqueueValidationSnackbar(error as Error);
			} finally {
				setAssigningCustomer(false);
			}
			return;
		}

		//For DevelopersManageRolesPage
		try {
			await api.setUserCustomer(selectedCustomer.customerNumber);

			//force reload to update user data - enough for internal purposes
			window.location.reload();
		} catch (error) {
			enqueueValidationSnackbar(error as Error);
		} finally {
			setAssigningCustomer(false);
		}
	};

	const handleSendCustomerButtonText = (): string => {
		if (!onlySelectionOfCustomer) {
			return 'Assign selected customer';
		}

		if (isEmpty(businessPartnerSearch)) {
			return t('qaAccess:form-search-business-partner');
		}

		if (isSelectedBusinessPartnerAlreadyAssigned) {
			return t('qaAccess:form-selected-business-partner-already-chosen');
		}

		return t('qaAccess:form-select-business-partner');
	};

	return (
		<Box sx={{ p: padding }}>
			<Box
				sx={{
					display: 'flex',
					gap: 3,
					flexDirection: { xs: 'column', md: 'row' },
				}}>
				<Box sx={{ flex: 1 }}>
					<SearchProductForm
						key={`business-partner-search-${clearSearchTrigger}`}
						defaultInput={businessPartnerSearch}
						onInputChange={setBusinessPartnerSearch}
						onInputClear={() => setBusinessPartnerSearch('')}
						label={t(
							'maintenancePlanner:search-business-partner-helper-text'
						)}
						sx={{ width: 1 }}
					/>
					<Button
						variant="contained"
						color="primary"
						fullWidth
						sx={{ mt: 4 }}
						disabled={
							isEmpty(businessPartnerSearch) ||
							selectedBusinessPartnerId.ids.size === 0 ||
							isSelectedBusinessPartnerAlreadyAssigned ||
							assigningCustomer
						}
						onClick={() => {
							void handleSendCustomer();
						}}>
						{handleSendCustomerButtonText()}
					</Button>
				</Box>
				<Box
					sx={{
						flex: 1,
						bgcolor: 'background.paper',
						borderRadius: 2,
						boxShadow: 1,
						p: 3,
						minHeight: 120,
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
					}}>
					<Typography variant="subtitle1" sx={{ mb: 2 }}>
						{assignedCustomerHeader}
					</Typography>
					{displayedCustomer && (
						<>
							<Box sx={{ mb: 1 }}>
								<strong>
									{t(
										'maintenancePlanner:search-business-partner-helper-text'
									)}
									:
								</strong>{' '}
								{displayedCustomer.customerName}
							</Box>
							<Box sx={{ mb: 1 }}>
								<strong>
									{t(
										'maintenancePlanner:business-partner-number'
									)}
									:
								</strong>{' '}
								{displayedCustomer.customerNumber}
							</Box>
							<Box
								sx={{
									mb: 1,
									display: 'flex',
									alignItems: 'center',
								}}>
								<strong>{t('common:country')}:</strong>
								<Box sx={{ ml: 1 }}>
									<LocationCountryText
										showCountryCode={showCountryCode}
										qaAccessForm={qaAccessForm}
										countryCode={
											displayedCustomer.customerCountry
										}
									/>
								</Box>
							</Box>
						</>
					)}
				</Box>
			</Box>
			<Box sx={{ mt: 3 }}>
				<VolvoDataGrid
					isLoading={isLoadingBusinessPartners}
					rows={businessPartnerRows}
					columns={businessPartnerColumns}
					isInnerGrid
					hideFooter
					disableMultipleRowSelection
					onRowSelectionModelChange={(newSelectionModel) =>
						setSelectedBusinessPartnerId(newSelectionModel)
					}
					rowSelectionModel={selectedBusinessPartnerId}
				/>
			</Box>
		</Box>
	);
};
